<template>
  <BaseModal @close="handleClose" cardWidth="600px" noPadding noToolbar noExit>
    <template #default>
      <div class="modal-content relative w-full" 
      :class="{'generating-listing': generatingListing, 'generation-failed': !generatingListing && showGenerationFailed}">
        <!-- FORM STATE -->
        <transition name="form-state">
          <div v-if="!generatingListing && !showGenerationFailed" class="w-full flex flex-col">
            <!-- Header -->
            <div class="relative w-full flex gap-2 p-5 border-b border-border-normal">
              <AgencyDirectorySettingsIcon class="text-icon-muted" />
              <div class="flex-grow flex flex-col gap-1">
                <BaseText type="label" size="sm" class="text-text-muted">
                  Create your Agency Listing
                </BaseText>
                <BaseText type="body" size="sm" class="text-text-normal">
                  We'll prefill your listing based on your online presence.
                </BaseText>
              </div>
              <button class="absolute top-2 right-2 p-1.5" @click="handleClose">
                <TagRemoveIcon stroke="#5E6678" :width="16" :height="16" />
              </button>
            </div>
            <!-- Form Body -->
            <div class="w-full flex flex-col p-5 border-b border-border-normal">
              <!-- Agency Website Input -->
              <div class="w-full flex flex-col gap-1">
                <div class="w-full flex items-center justify-between">
                  <BaseText type="label" size="sm" class="text-text-muted">
                    Website
                  </BaseText>
                  <transition>
                    <div v-if="submitAttempted && !agencyWebsiteValid" class="flex items-center gap-0.5">
                      <InfoIcon class="text-secondary-red-100" :width="18" :height="18" />
                      <BaseText type="body" size="xs" class="text-secondary-red-100">
                        Enter a valid URL
                      </BaseText>
                    </div>
                  </transition>
                </div>
                <input v-model="agencyWebsiteUrl" :class="{'invalid': submitAttempted && !agencyWebsiteValid}" 
                class="form-input min-w-0 w-full px-2 py-1.5" placeholder="https://www.youragency.com" />
              </div>
              <!-- LinkedIn Input -->
              <div class="w-full flex flex-col gap-1 mt-5">
                <div class="w-full flex items-center justify-between">
                  <BaseText type="label" size="sm" class="text-text-muted">
                    Company LinkedIn Profile
                  </BaseText>
                  <transition>
                    <div v-if="submitAttempted && !linkedInValid" class="flex items-center gap-0.5">
                      <InfoIcon class="text-secondary-red-100" :width="18" :height="18" />
                      <BaseText type="body" size="xs" class="text-secondary-red-100">
                        Enter a valid company URL
                      </BaseText>
                    </div>
                  </transition>
                </div>
                <input v-model="linkedInUrl" :class="{'invalid': submitAttempted && !linkedInValid}" 
                class="form-input min-w-0 w-full px-2 py-1.5" placeholder="https://www.linkedin.com/company/your-agency" />
              </div>
            </div>
            <!-- Actions Footer -->
            <div class="w-full flex items-center justify-between p-5">
              <button class="px-2.5 py-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-25"
              @click="handleClose">
                <BaseText type="label" size="sm" class="text-text-muted">
                  Cancel
                </BaseText>
              </button>
              <button class="submit-btn px-2.5 py-1.5 rounded-md" :class="submitEnabled ? 'enabled' : 'disabled cursor-default'"
              :disabled="!submitEnabled" @click="handleSubmit">
                <BaseText type="label" size="sm" class="transition-colors duration-100" :class="submitEnabled ? 'text-white' : 'text-text-disabled'">
                  Continue
                </BaseText>
              </button>
            </div>
          </div>
        </transition>
        <!-- GENERATING LISTING STATE -->
        <transition name="generating-state">
          <div v-if="generatingListing">
            <div class="w-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
              <!-- Animation -->
              <video src="https://publicassets.foreplay.co/white-ai-loader.webm" style="width: 100px; height: 100px;" autoplay muted loop class="relative bottom-2" />
              <!-- Loading Text -->
              <BaseText type="label" size="sm" class="text-text-muted relative bottom-2">
                Generating your listing...
              </BaseText>
              <BaseText type="body" size="sm" class="text-text-normal mt-1 relative bottom-2">
                This may take up to a minute.
              </BaseText>
            </div>
          </div>
        </transition>
        <!-- GENERATION FAILED STATE -->
        <transition name="generation-state">
          <div v-if="!generatingListing && showGenerationFailed" class="w-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
            <NoSearchResultsIcon light-mode :width="80" :height="80" />
            <BaseText type="label" size="sm" class="text-text-muted">
              We couldn't access your agency's website
            </BaseText>
            <BaseText type="body" size="sm" class="text-text-normal mt-1">
              How would you like to continue?
            </BaseText>
            <button class="submit-btn px-2.5 py-1.5 rounded-md mt-6" @click="handleContinue">
              <BaseText type="label" size="sm" class="text-white">
                Create Manually
              </BaseText>
            </button>
            <button class="px-2.5 py-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-25 mt-2"
            @click="showGenerationFailed = false">
              <BaseText type="label" size="sm" class="text-text-muted">
                Try Again
              </BaseText>
            </button>
          </div>
        </transition>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import { mapGetters } from 'vuex'
import ForeplayAPI from '@/api/foreplayServer'

import AgencyDirectorySettingsIcon from '../../globals/Icons/SettingsIcons/AgencyDirectorySettingsIcon.vue'
import TagRemoveIcon from '../../globals/Icons/TagRemoveIcon.vue'
import InfoIcon from '../../globals/Icons/InfoSimpleIcon.vue'
import NoSearchResultsIcon from '../../globals/Icons/SidebarIcons/NoSearchResultsIcon.vue'

export default {
  name: 'CreateAgencyListingModal',
  components: {
    AgencyDirectorySettingsIcon,
    TagRemoveIcon,
    InfoIcon,
    NoSearchResultsIcon
  },
  data () {
    return {
      agencyWebsiteUrl: '',
      linkedInUrl: '',
      submitAttempted: false,
      generatingListing: false,
      showGenerationFailed: false,
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getTeam']),
    submitEnabled () {
      return !!(this.agencyWebsiteUrl?.length && this.linkedInUrl?.length)
    },
    agencyWebsiteValid () {
      const url = this.agencyWebsiteUrl.trim()
      if (!url.length) return false
      const re = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/[^\s]*)?$/
      return re.test(url)
    },
    linkedInValid () {
      const url = this.linkedInUrl.trim()
      if (!url.length) return false
      const re = /^(https?:\/\/)?(www\.)?linkedin\.com\/company\/[a-zA-Z0-9-]+\/?$/i
      return re.test(url)
    }
  },
  methods: {
    handleClose () {
      console.log('handleClose')
      if (this.generatingListing) return
      this.$emit('close')
    },
    async handleSubmit () {
      this.submitAttempted = true
      if (!this.agencyWebsiteValid || !this.linkedInValid) return

      const formattedWebsiteUrl = formatUrl(this.agencyWebsiteUrl)
      const formattedLinkedInUrl = formatUrl(this.linkedInUrl)

      this.generatingListing = true
      try {
        const agencyListing = await ForeplayAPI.Agency.generateAgencyListing(formattedWebsiteUrl, formattedLinkedInUrl)
        this.$emit('generatedListing', {
          teamId: this.getTeam.id,
          agencyWebsite: formattedWebsiteUrl,
          agencyLinkedInProfile: formattedLinkedInUrl,
          services: [],
          industries: [],
          leadIntroEmail: '',
          ...agencyListing
        })
        this.$showAlert({
          type: 'success',
          message: 'Listing generated successfully'
        })
        this.$emit('close')
      } catch (error) {
        console.error('Error generating agency listing: ', error)
        this.showGenerationFailed = true
        this.generatingListing = false
        this.submitAttempted = false
      }
    },
    handleContinue () {
      this.$emit('generatedListing', {
        teamId: this.getTeam.id,
        agencyName: '',
        agencyWebsite: '',
        agencyLinkedInProfile: '',
        avatar: '',
        primaryCountry: null,
        services: [],
        industries: [],
        leadIntroEmail: '',
        aboutDescriptionHtml: '',
        whatSetsThemApartHtml: '',
        coreServicesHtml: '',
        metaDescription: ''
      });
      this.$emit('close')
    }
  }
}

const formatUrl = (url) => {
  let input = url.trim()
  if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(input)) {
    input = `https://${input}`
  }
  try {
    const u = new URL(input)
    if (!u.hostname.startsWith('www.')) {
      u.hostname = `www.${u.hostname}`
    }
    if (!u.pathname || !u.pathname.endsWith('/')) {
      u.pathname = (u.pathname && u.pathname.length ? `${u.pathname}/` : '/')
    }
    return `${u.protocol}//${u.host}${u.pathname}${u.search}${u.hash}`
  } catch {
    // Fallback (very simple)
    return input.endsWith('/') ? input : `${input}/`
  }
}
</script>

<style scoped>
.modal-content {
  height: 334px;
  transition: height 250ms ease-in-out;
  transition-delay: 200ms;
  overflow: hidden;
}
.modal-content.generating-listing {
  height: 202px;
}
.modal-content.generation-failed {
  height: 280px;
}

.form-state-leave-active {
  transition: opacity 200ms ease-in-out;
}
.form-state-leave-from {
  opacity: 1;
}
.form-state-leave-to {
  opacity: 0;
}
.generating-state-enter-active {
  transition: opacity 300ms ease-in-out;
  transition-delay: 200ms;
}
.generating-state-enter-from, .generating-state-enter {
  opacity: 0;
}
.generating-state-enter-to {
  opacity: 1;
}

.form-input {
  color: #06070F;
  border-radius: 6px;
  border: 1px solid;
  border-color: transparent;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: border-color 150ms ease-in-out, box-shadow 150ms ease-in-out;

  /* Body/Small */
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
}
.form-input:focus {
  outline: none;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.1), 0px 0px 0px 1px rgba(0, 56, 108, 0.1);
}
.form-input::placeholder {
  color: #5E6678;
  transition: color 150ms ease-in-out;
  opacity: 0.9;
}
.form-input.invalid {
  box-shadow: none;
  border-color: #FF002F;
}
.form-input:hover::placeholder {
  color: #303546;
}
.form-input:focus::placeholder {
  color: #C1C7D0;
}

@property --submit-btn-opacity-1 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
.submit-btn {
  --submit-color: #121212;
  background: linear-gradient(180deg, rgba(255, 255, 255, var(--submit-btn-opacity-1)) 0%, rgba(255, 255, 255, var(--submit-btn-opacity-2)) 100%), var(--submit-color);
  color: white;
  box-shadow: 0px -1px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px 0px 0px 1px var(--submit-color);
  transition: box-shadow 100ms ease-in-out,
    color 100ms ease-in-out,
    background-color 100ms ease-in-out,
    --submit-btn-opacity-1 100ms ease-in-out,
    --submit-btn-opacity-2 100ms ease-in-out,
    --submit-color 100ms ease-in-out;
}
.submit-btn.enabled:hover {
  --submit-btn-opacity-1: 0.24;
  --submit-btn-opacity-2: 0;
}
.submit-btn.disabled {
  color: #A4ABB8;
  box-shadow: none;
  --submit-btn-opacity-1: 0;
  --submit-btn-opacity-2: 0;
  --submit-color: #F6F8FA;
}

.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>